import styles from "./ChatHistoryDropdown.module.css";
import {useEffect, useRef, forwardRef, useState} from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import moment from "moment";
import Assistant from "../models/assistantModel";
import SidebarChatHistoryModel from "../models/sidebarChatHistoryModel";
import ComputerCronJobModel from "../models/computerCronJobModel";
import AskAddyV2 from "../models/askAddyV2";
import V2ChatsDrawer from "../askAddyV2/V2ChatsDrawer";
import { isProvisionalChatName } from "../utils/chatNaming";

/**
 * Builds a human-readable info string for a chat row tooltip.
 * Shows last active timestamp and loan borrower/number if available.
 * @param {Object} chat - Chat object from chatHistory state
 * @param {Object|null} loanInfo - { borrowers, loanNumber } fetched from backend, or null
 */
const buildChatInfoText = (chat, loanInfo) => {
    const lines = [];

    if (chat.loanId) {
        if (loanInfo) {
            if (loanInfo.borrowers && loanInfo.borrowers.length > 0) {
                const names = loanInfo.borrowers
                    .map((b) => `${b.firstName || ""} ${b.lastName || ""}`.trim())
                    .filter(Boolean);
                if (names.length > 0) {
                    lines.push(`Borrower${names.length > 1 ? "s" : ""}: ${names.join(", ")}`);
                }
            }
        }
    }

    if (chat.originalLastActive) {
        lines.push(`Last active: ${moment(chat.originalLastActive).format("MMM D, YYYY [at] h:mm A")}`);
    }

    return lines.join("\n") || "No details available";
};

const ChatHistoryDropdown = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const displayPosition = props.displayPosition;
    const referencedElementId = props.referencedElementId;
    const today = moment();
    const weekStart = moment().startOf("week");
    const monthStart = moment().startOf("month");

    // Initialize the chatHistory state with an empty array
    const [chatHistory, setChatHistory] = useState([]);
    const [updatedPreviousChatNames, setUpdatedPreviousChatNames] = useState(false);

    // Cache of { [loanId]: { borrowers, loanNumber } } fetched from backend
    const [loanInfoCache, setLoanInfoCache] = useState({});

    const [activeJobCount, setActiveJobCount] = useState(0);

    // Rename state: tracks which chat is being renamed and the current input value
    const [renamingChatId, setRenamingChatId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const renameInputRef = useRef(null);

    const formatChatData = (data) => {
        return Object.keys(data).map((chatID) => {
            const chat = data[chatID];
            const lastActive = moment(chat.lastActive);
            let formattedDate;
            if (lastActive.isSame(today, "d")){
                formattedDate = "Today";
            } else if (lastActive.isBetween(weekStart, today)){
                formattedDate = "Last 7 days";
            } else if (lastActive.isBetween(monthStart, today)){
                formattedDate = lastActive.format("MMMM");
            } else {
                formattedDate = lastActive.format("YYYY");
            }
            // If there's chat clientHostname, replace www. with an empty string
            // If no clientHostname, return empty string
            const clientHostname = chat.clientHostname ? chat.clientHostname.replace("www.", "") : "unknown site";
            return {...chat, 
                    lastActive: formattedDate,
                    clientHostname: clientHostname,
                    originalLastActive: chat.lastActive}; // Keep raw date for sorting
        }).sort((a, b) => new Date(b.originalLastActive) - new Date(a.originalLastActive));
    };
    

    const initializeChatHistory = async () => {
        // Build merged chat index: DB + localStorage (DB takes precedence, legacy from localStorage)
        let mergedChats = {};
        const uid = props.uid;

        if (uid) {
            const dbChats = await SidebarChatHistoryModel.listChats(uid);
            dbChats.forEach((c) => {
                mergedChats[c.chatId] = {
                    chatID: c.chatId,
                    chatName: c.chatName,
                    clientHostname: c.clientHostname || "unknown site",
                    publicId: c.publicId,
                    appID: c.appID,
                    dateStarted: c.dateStarted,
                    lastActive: c.lastActive,
                    loanId: c.loanId,
                    // Keep raw timestamps for the info tooltip
                    originalDateStarted: c.dateStarted,
                };
            });
        }

        const sidebarChats = JSON.parse(localStorage.getItem("addy-ai-sidebar-chats")) || {};
        Object.keys(sidebarChats).forEach((chatID) => {
            if (!mergedChats[chatID]) {
                mergedChats[chatID] = { ...sidebarChats[chatID], chatID };
            }
        });

        const chatIDs = Object.keys(mergedChats);
        if (!(chatIDs && chatIDs.length)) {
            setChatHistory([]);
            return;
        }

        oneTimePreviousChatName(mergedChats);
        const formattedChatHistory = formatChatData(mergedChats);
        setChatHistory(formattedChatHistory);
        fetchLoanInfoForChats(formattedChatHistory);

        const lastChatId = localStorage.getItem("addy-ai-sidebar-last-chat-id");
        if (lastChatId && mergedChats[lastChatId]) {
            mergedChats[lastChatId].lastActive = Date.now();
            localStorage.setItem("addy-ai-sidebar-chats", JSON.stringify(mergedChats));
        }
    };

    const oneTimePreviousChatName = async (sidebarChats) => {
        if (updatedPreviousChatNames) {
            console.log("Previous chat names already updated");
            return;
        }

        const chatIDs = Object.keys(sidebarChats);
        const newSidebarChats = {...sidebarChats};
        // Track which chatIDs got AI-generated names so we can sync them to the backend
        const renamedChatIDs = [];
        for (let i = 0; i < chatIDs.length; i++) {
            const chatID = chatIDs[i];
            const clientHostname = sidebarChats[chatID].clientHostname;
            const chatName = sidebarChats[chatID].chatName;
            if (!chatID || !clientHostname) {
                continue;
            }
            
            const chatHistory = JSON.parse(localStorage.getItem(chatID))
            if (!(chatHistory && chatHistory?.messages && chatHistory?.messages?.length)) {
                continue;
            }
            // Chat history exists. Get the first message
            const firstMessage = chatHistory.messages[0]?.message;
            if (!firstMessage) {
                continue;
            }
            // Only chats still carrying a placeholder title need an AI-generated one
            if (!isProvisionalChatName({ chatName, clientHostname, firstMessage })) {
                continue;
            }
            const chatNameFromModel = await Assistant.getChatName(firstMessage);
            if (!chatNameFromModel) {
                continue;
            }
            // Update the newSidebarChats.chatID.chatName
            newSidebarChats[chatID].chatName = chatNameFromModel;
            renamedChatIDs.push({ chatID, chatName: chatNameFromModel });
        }
        // Persist to localStorage
        if (renamedChatIDs.length > 0) {
            localStorage.setItem("addy-ai-sidebar-chats", JSON.stringify(newSidebarChats));
            // Sync AI-generated names to backend so they persist cross-device
            if (props.uid) {
                renamedChatIDs.forEach(({ chatID, chatName }) => {
                    SidebarChatHistoryModel.renameChat(props.uid, chatID, chatName);
                });
            }
        }
        setUpdatedPreviousChatNames(true);
    };

    /**
     * Lazily fetches loan info (borrower names + loan number) for all loan-associated chats.
     * Results are cached in loanInfoCache keyed by loanId to avoid redundant requests.
     * Requires props.organizationId to be set; silently skips if not available.
     */
    const fetchLoanInfoForChats = async (chats) => {
        if (!props.uid || !props.organizationId) return;

        const loanIds = [...new Set(
            chats
                .filter((c) => c.loanId && !loanInfoCache[c.loanId])
                .map((c) => c.loanId)
        )];

        if (!loanIds.length) return;

        const results = await Promise.all(
            loanIds.map((loanId) =>
                SidebarChatHistoryModel.getLoanInfo(props.uid, loanId, props.organizationId)
                    .then((info) => ({ loanId, info }))
            )
        );

        const newEntries = {};
        results.forEach(({ loanId, info }) => {
            // Store null explicitly so we don't re-fetch failed lookups
            newEntries[loanId] = info || null;
        });

        setLoanInfoCache((prev) => ({ ...prev, ...newEntries }));
    };

    const fetchActiveJobCount = async () => {
        if (!props.uid) return;
        try {
            const jobs = await ComputerCronJobModel.list(props.uid);
            setActiveJobCount(jobs.filter((j) => j.enabled).length);
        } catch { /* silent */ }
    };

    // Ask Addy v2 shows its own drawer (below), which loads its chats a page at a time; the
    // classic list (every chat with its messages, plus a loan lookup per loan) would go unseen.
    useEffect(() => {
        if (AskAddyV2.isEnabled()) return;
        initializeChatHistory();
        displayDropdownUnder();
        fetchActiveJobCount();
    }, []);

    useEffect(() => {
        if (AskAddyV2.isEnabled()) return;
        if (props.isDrawerOpen) {
            initializeChatHistory();
            fetchActiveJobCount();
        }
    }, [props.isDrawerOpen, props.chatListVersion]);

    const onChatTitleClick = (chatID) => {
        // Call the parent component's function to open the chat
        props.onChatTitleClick(chatID);
    };

    const onCreateNewChatClick = () => {
        // Call the parent component's function to create a new chat
        props.onCreateNewChatClick();
    };

    const startRenaming = (e, chatID, currentName) => {
        e.stopPropagation();
        setRenamingChatId(chatID);
        setRenameValue(currentName);
        // Focus the input after render
        setTimeout(() => {
            if (renameInputRef.current) {
                renameInputRef.current.focus();
                renameInputRef.current.select();
            }
        }, 50);
    };

    const commitRename = async (chatID) => {
        const trimmed = renameValue.trim();
        if (!trimmed || !chatID) {
            setRenamingChatId(null);
            return;
        }

        // Update in the chat index (localStorage)
        const savedChats = JSON.parse(localStorage.getItem("addy-ai-sidebar-chats")) || {};
        if (savedChats[chatID]) {
            savedChats[chatID].chatName = trimmed;
            localStorage.setItem("addy-ai-sidebar-chats", JSON.stringify(savedChats));
        }

        // Update in the individual chat record (localStorage)
        const chatRecord = JSON.parse(localStorage.getItem(chatID));
        if (chatRecord) {
            chatRecord.chatName = trimmed;
            localStorage.setItem(chatID, JSON.stringify(chatRecord));
        }

        // Update in the backend (DB)
        if (props.uid) {
            SidebarChatHistoryModel.renameChat(props.uid, chatID, trimmed);
        }

        // Notify parent so the active chat header updates if needed
        if (props.onRenameChat) {
            props.onRenameChat(chatID, trimmed);
        }

        // Refresh local state
        setChatHistory((prev) =>
            prev.map((chat) =>
                chat.chatID === chatID ? { ...chat, chatName: trimmed } : chat
            )
        );
        setRenamingChatId(null);
    };

    const cancelRename = () => {
        setRenamingChatId(null);
        setRenameValue("");
    };

    const handleRenameKeyDown = (e, chatID) => {
        if (e.key === "Enter") {
            commitRename(chatID);
        } else if (e.key === "Escape") {
            cancelRename();
        }
    };

    const renderChatHistory = () => {
        const dates = [...new Set(chatHistory.map((chat) => chat.lastActive))];

        if (chatHistory.length === 0) {
            return (<div className={styles.noChats}>
                No chats yet.
            </div>);
        }

        const chatHistoryList = () => {
            return dates.map((date) => (
                <div key={date}>
                    <div className={styles.sectionTitle} style={{
                        paddingLeft: "8px",
                    }}>{date}</div>
                    {chatHistory.filter((chat) =>
                        chat.lastActive === date).map((chat) => {
                        // Determine display name - show loan indicator if chat is loan-associated
                        const displayName = chat.chatName 
                            ? chat.chatName 
                            : `${chat.lastActive} - ${chat.clientHostname}`;
                        const tooltipText = chat.loanId 
                            ? `Loan Chat: ${displayName}` 
                            : displayName;
                        
                        const isRenaming = renamingChatId === chat.chatID;

                        return (
                            <div
                                key={chat.chatID}
                                className={`${styles.chatTitle} ${styles.chatTitleRow}`}
                                onClick={() => !isRenaming && onChatTitleClick(chat.chatID)}
                            >
                                {/* Loan indicator icon */}
                                {chat.loanId && (
                                    <span
                                        className={styles.loanIcon}
                                        title="Loan Chat"
                                    >
                                        📋
                                    </span>
                                )}

                                {isRenaming ? (
                                    <input
                                        ref={renameInputRef}
                                        className={styles.renameInput}
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onKeyDown={(e) => handleRenameKeyDown(e, chat.chatID)}
                                        onBlur={() => commitRename(chat.chatID)}
                                        onClick={(e) => e.stopPropagation()}
                                        maxLength={80}
                                    />
                                ) : (
                                    <Tooltip title={tooltipText} arrow={false}>
                                        <span className={styles.chatTitleText}>
                                            {displayName}
                                        </span>
                                    </Tooltip>
                                )}

                                {/* Info button — shown on hover, displays timestamp + loan details */}
                                {!isRenaming && (
                                    <Tooltip
                                        title={
                                            <span style={{ whiteSpace: "pre-line", fontSize: "12px", lineHeight: "1.6" }}>
                                                {buildChatInfoText(chat, chat.loanId ? loanInfoCache[chat.loanId] : null)}
                                            </span>
                                        }
                                        arrow={false}
                                        placement="bottom"
                                    >
                                        <button
                                            className={styles.infoBtn}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label="Chat info"
                                        >
                                            i
                                        </button>
                                    </Tooltip>
                                )}

                                {/* Pencil rename button — shown on hover via CSS */}
                                {!isRenaming && (
                                    <Tooltip title="Rename" arrow={false}>
                                        <button
                                            className={styles.renameBtn}
                                            onClick={(e) => startRenaming(e, chat.chatID, displayName)}
                                            aria-label="Rename chat"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                            </svg>
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}
                </div>
            ));
        };

        return <div className={styles.recentChatDirectoryList}>
            <div className={styles.infoHelperText} style={{paddingLeft: "8px", paddingBottom: "8px"}}>
                Recent chats
            </div>
            {chatHistoryList()}
            {/* <br></br>
            <div className={styles.infoHelperText}>
                Your chats are only saved on this device.
            </div> */}
        </div>;
    };

    // Function to display dropdown under the referenced element
    const displayDropdownUnder = () => {
        // eslint-disable-next-line no-undef
        const refElement = document.getElementById(referencedElementId);
        const dropdown = dropdownRef.current;

        if (refElement && dropdown) {
            const rect = refElement.getBoundingClientRect();
            let top = rect.bottom;
            const right = 0;

            // Modify top value based on displayPosition
            if (displayPosition === "top") {
                top = rect.top - dropdown.offsetHeight;
            }

            dropdown.style.position = "absolute";
            dropdown.style.top = `${top + 5}px`;
            dropdown.style.right = `${right}px`;
        }
    };

    // Ask Addy v2 keeps its conversations on the v2 server and has its own drawer (board 7).
    if (AskAddyV2.isEnabled()) {
      return (
        <div ref={ref}>
          <V2ChatsDrawer
            // Another user's sign-in starts their own list of chats.
            key={props.uid}
            uid={props.uid}
            organizationId={props.organizationId}
            loanId={props.v2LoanId}
            scenarioId={props.v2ScenarioId}
            activeThreadId={props.v2ThreadId}
            isDrawerOpen={props.isDrawerOpen}
            onThreadClick={props.onV2ThreadClick}
            onNewChat={onCreateNewChatClick}
            onOpenApps={props.onOpenV2Apps}
            isSearchRequested={props.isV2SearchRequested}
            onSearchShown={props.onV2SearchShown}
            clientChat={props.v2ClientChat}
            onOpenClientChat={props.onOpenV2ClientChat}
            onCollapse={props.toggleDrawer}
            assistant={props.v2Assistant}
            onOpenProfile={props.onOpenV2Profile}
          />
        </div>
      );
    }

    return (
      <div
        ref={dropdownRef}
        className={styles.assistants}
        style={{ overflowY: "auto" }}
      >
        <img className={styles.plusIcon} alt="" src="/plus.svg" />
        <div className={styles.frameParent} ref={ref}>
          <div className={styles.frameGroup}>
          
            <Tooltip
                key={"toggle-sidebar-1"}
                title={"Toggle sidebar"}
                arrow>
                <div className="drawer-button dropdown-button" onClick={props.toggleDrawer}>
                    <img width="20" height="20" alt="Toggle sidebar" src="/open-left-drawer.svg" />
                </div>
            </Tooltip>
                    
            <div className={styles.h2HeadingWrapper}>
              <div
                onClick={() => onCreateNewChatClick()}
                className={styles.h2Heading}
              >
                New chat &nbsp;&nbsp;+
              </div>
            </div>
            {/* <div className={styles.div}>+</div> */}
          </div>
          <div
            className={styles.scheduledTasksLink}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              navigate(`/scheduled-tasks?${params.toString()}`);
            }}
          >
            <ScheduleIcon sx={{ fontSize: 16, color: "var(--text-secondary)" }} />
            <span>Scheduled Tasks</span>
            {activeJobCount > 0 && (
              <span className={styles.scheduledBadge}>{activeJobCount}</span>
            )}
          </div>
          {renderChatHistory()}
        </div>
      </div>
    );
});

export default ChatHistoryDropdown;
